<template lang="pug">
  .pdp-cta-v3.brand-color-1(:class="{ 'waitlist': showWaitlistForm }" itemprop="offers" itemtype="http://schema.org/Offer" itemscope)
    meta(itemprop="itemCondition" content="https://schema.org/NewCondition")
    meta(itemprop="availability" content="https://schema.org/InStock" v-if="product.inventory_status === 'active'")
    meta(itemprop="price" :content="product.price")
    meta(itemprop="priceCurrency", content="USD")

    .waitlist-section(v-if="showWaitlistForm")
      .stock-warning-msg.row
        .xs-2.text-center(:class="{ 'xs-pt-50m': product.preorder_promise_date }")
          i.icon-warning.xs-f-xlarge.max-at-tweak

        .xs-10.text-left
          p.xs-f-xsmall.lg-f-small.max-at-tweak.bold(:class="{ 'xs-pt-25m': !product.preorder_promise_date }") Temporarily out of stock.
          p.xs-f-xsmall.lg-f-small.max-at-tweak(v-if="product.preorder_promise_date") Expected availability the week of {{ $formatDate(product.preorder_promise_date, 'MM/DD') }}.

      .help-msg.xs-pt-100m.xs-pb-150m.side-padding
        p.xs-f-small.lg-f-small.max-at-tweak.text-left(v-if="advisorCompleted && product.product_type == 'color_kit'") Click '+ More Colors' to see your other recommended shades.

      WaitlistForm.xs-ml-10pct.xs-mr-10pct(:product-id="product.id" :inventory-status="product.inventory_status")

    .waitlist-section(v-else-if="isProductDiscontinued")
      .discontinued-msg.row
        i.icon-warning.xs-f-xlarge.max-at-tweak
        p.xs-f-xsmall.lg-f-small.max-at-tweak.bold This product has been discontinued.

    .purchase-box.xs-12(v-else)
      .radio-block
        .radio-wrap
          .radio.clickable(@click="setPurchaseType('one-time')" :class="{ 'selected': purchaseType == 'one-time' }")
            .top-wrap
              MrRadio(
                value="one-time" 
                :model-value="purchaseType" 
                @change="setPurchaseType('one-time')" 
                name="purchase type" 
                bullseye
                aria-label="One-time purchase"
              )
              span.sub-inner
                .first-line
                  span.semi-bold.xs-f-small.md-f-medium.max-at-tweak {{ product.onPagePurchaseBox.oneTimeOrderText }}
                  span.float-right.price.semi-bold.xs-f-small.md-f-medium.max-at-tweak  {{ $currencyShort(product.price) }}

          .radio.clickable(@click="setPurchaseType('subscription')" :class="{ 'selected': purchaseType == 'subscription' }")
            .top-wrap
              MrRadio(
                value="subscription" 
                :model-value="purchaseType" 
                @change="setPurchaseType('subscription')" 
                name="purchase type" 
                bullseye
                aria-label="Subscription purchase"
              )
              .sub-inner
                .first-line
                  span.semi-bold.xs-f-small.md-f-medium.max-at-tweak {{ product.onPagePurchaseBox.subOrderText }}
                  .float-right
                    span.strike.price.text-color-3.xs-f-small.md-f-medium.max-at-tweak.pr-25m {{ $currencyShort(product.price) }}
                    span.price.semi-bold.xs-f-small.md-f-medium.max-at-tweak  {{ $currencyShort(product.sub_price) }}
                .description-line(:class="{'mt-10': version == 'B'}")
                  span.xs-f-xsmall.max-at-tweak(:class="{'light-grey': version == 'B', 'brand-color-1': version == 'C'}") {{ description }}

            .member-benefits.brand-color-1(v-if="version == 'C'")
              ul
                li(v-html="benefit" v-for="benefit in memberBenefits")

            .dropdown-wrap.mt-10(v-if="showFrequencyDropdown" :class="{'version-b': version == 'B'}")
              MrDropdown.full-width(label="Deliver every: " bold-options new-theme :options="subFrequencyOptions" :value="subFrequency" @input="setSubFrequency")

      .extra-tube(v-if="showExtraTube")
        p.extra-tube-title.xs-f-small.md-f-medium.semi-bold.max-at-tweak.brand-color-1 {{ product.onPagePurchaseBox.extraTubeTitleText }}
        MrInputCheckbox.extra-tube-checkbox.mt-10.mb-10.checkbox.xs-f-xsmall.sm-f-small.max-at-tweak(:label="extraTubeText" v-model="extraTubeModel" data-mr-ass="extra-tube-input")
              
</template>
<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
  import MrRadio from '@components/mrRadio';
  import MrDropdown from '@components/MrDropdown';
  import MrInputCheckbox from '@components/mrInputCheckbox';
  import MrBtn from '@components/MrBtn';
  import StickyWrap from '@components/StickyWrap/stickyWrap';
  import WaitlistForm from "@components/PDP/WaitlistForm";
  import MountedFlag from "@components/MountedFlag";
  import PurchasePanelMixin from "@components/PDP/PurchasePanelMixin";
  import ChangeColorCTAs from "@components/PDP/PdpCTAs/ChangeColorCTAs";

  export default {
    name: "OnPagePurchaseBox",

    components: {
      MrRadio,
      MrDropdown,
      MrInputCheckbox,
      MrBtn,
      StickyWrap,
      WaitlistForm,
      MountedFlag,
      ChangeColorCTAs,
    },

    mixins: [PurchasePanelMixin],

    props: {
      version: {
        type: String,
        default: 'B'
      }
    },

    computed: {
      ...mapState('global', ['windowData']),

      ...mapState('pdp', [
        'product', 
        'purchaseType',
        'subFrequency', 
        'qty',
        'attachableProdIds',
        'subscription',
        'googleShopping',
        'extraTube',
      ]),

      ...mapState('colorAdvisor', ['advisorCompleted']),
      ...mapState('cart', ['addingProduct']),

      ...mapGetters('pdp', [
        'subFrequencies',
        'isPurePigment',
      ]),

      ...mapGetters('colorAdvisor', [
        'advisorFrequency'
      ]),

      ...mapGetters('refillCart', [
        'productMaxQtyInRefillCart',
        'baseSubscription',
        'refillCartIsApplePay'
      ]),

      subFrequenciesAvailable() {
        return this.subFrequencies.length > 1;
      },

      subFrequencyOptions() {
        return this.subFrequencies.map(frequency => {
          return {
            value: frequency,
            label: frequency + " weeks" + (this.advisorFrequency == frequency ? " (Recommended)" : ""),
          };
        });
      },

      showFrequencyDropdown() {
        return this.purchaseType == 'subscription';
      },

      showExtraTube() {
        return Boolean(this.productIsRCC || this.productIsColorWonder);
      },

      productIsRCC() {
        return this.product && this.product.product_type == 'color_kit';
      },

      productIsColorWonder() {
        return this.product && this.product.product_type == 'colorwonder';
      },

      extraTubeText() {
        return this.product && this.product.onPagePurchaseBox.extraTubeText;
      },

      itemsToAdd() {
        var items = [
          {
            product_id: this.product.id,
            subscription: this.isSubscription ? this.subFrequency : 0,
            addExtraTube: this.extraTubeModel,
            attachableProdIds: this.attachableProdIds,
            qty: this.qty,
          },
        ];

        return items;
      },

      showWaitlistForm() {
        return ['coming_soon', 'backordered'].includes(this.product.inventory_status);
      },

      isProductDiscontinued() {
        return ['discontinued'].includes(this.product.inventory_status);
      },

      isSubscription() {
        return this.purchaseType === 'subscription';
      },

      purchasePanelSubscriptionClasses() {
        return {
          selected: this.isSubscription,
          disabled: this.addingProduct,
        };
      },

      isMobile() {
        return this.windowData.breakpoint === 'mobile';
      },

      addExtraPadding() {
        return this.windowData.isIosDevice && this.windowData.mobileNavHidden;
      },

      isColorWonder() {
        return this.product?.product_type == 'colorwonder';
      },

      activeSubAndSameProductNotSelected() {
        return Boolean(this.subscription.id && !this.sameProductInSub);
      },

      sameProductInSub() {
        let subedProdId = this.subscription?.product?.id;
        return this.product?.id == subedProdId;
      },

      guaranteeText() {
        return this.product.guaranteeText;
      },

      canAttachThisItem() {
        return (this.baseSubscription.subscriptionAttachments || []).some(att => att.code == this.product.product_type) && !this.refillCartIsApplePay;
      },

      //- as long as user has active sub in refill cart, any item should be available as one-time addon
      canOneTimeAddToOrder() {
        return Boolean(this.baseSubscription.id) && !this.refillCartIsApplePay;
      },

      addingToCart() {
        return this.addingProduct || Boolean(this.modifyingRefillProduct);
      },

      memberBenefits() {
        return this.product.onPagePurchaseBox.membershipBenefits;
      },

      description() {
        return this.version == "B" ? this.product?.onPagePurchaseBox.subDescription : this.product?.onPagePurchaseBox.shortSubDescription;
      },

      extraTubeModel: {
        get() {
          return this.extraTube;
        },

        set(val) {
          this.setExtraTube(val);
        }
      },

      birthday_day: {
        get() {
          return this.$store.state.customer.customerEditForm.birthday_day;
        },

        set(val) {
          this.$store.commit('customer/setBirthdayDay', val);
          this.$store.dispatch('customer/updateBirthday');
        }
      },
    },

    mounted() {
      this.getPreOrderId();
      this.setGoogleShoppingPurchaseType();
      this.updateSubFrequencyToProdType();
    },

    methods: {
      ...mapMutations('pdp', [
        'setPurchaseType',
        'setSubFrequency',
        'clearAttachableProdIds',
        'setExtraTube'
      ]),

      ...mapActions('pdp', [
        'openPdpXsell',
        'cancelPreOrder',
        'getPreOrderId'
      ]),

      ...mapActions('colorAdvisor', ['initColorAdvisor']),
      ...mapActions(['notifyError']),
      ...mapActions('cart', ['addMultipleItemsToCart']),

      async addToBag() {
        if (this.addingToCart) {return;}
        const ctaSource = `PDP - On page purchase box ${this.purchaseType}, ${this.product.name}`;

        await this.addMultipleItemsToCart({
          ctaSource,
          items: this.itemsToAdd,
        });

        this.clearAttachableProdIds();
        this.openPdpXsell();
      },

      setGoogleShoppingPurchaseType() {
        if (this.googleShopping) {
          this.setPurchaseType('one-time');
        } else {
          this.setPurchaseType('subscription');
        }
      },

      updateSubFrequencyToProdType() {
        let defaultFrequency = this.advisorFrequency || this.product.product_type_sub_frequency_default;
        this.setSubFrequency(defaultFrequency);
      },

      handleBestMatchCtaClick() {
        this.trackMREvent('Color Kit Hero V2 - Find My Shade');
        location.href = '/color-advisor';
      }
    }
  };
</script>
<style scoped lang="stylus">
  .pdp-cta-v3
    .purchase-box
      padding-bottom 14px
      .radio-block
        .radio-wrap
          width 100%
          .radio
            width 100%
            padding 14px
            display flex
            flex-direction column
            align-items normal
            border 1px solid #CBC6CB
            margin 1em auto
            place-items normal

            &.selected
              background-color #F8F7F8
              border 1px solid #3B2E4B
          .top-wrap
            display flex
            flex-direction row
            justify-content space-between
            align-items top

            .sub-inner
              display flex
              flex-direction column
              justify-content space-between
              align-items normal
              width 100%
              padding-left 14px
              font-style normal
              font-weight 600
              line-height 120%
              letter-spacing -0.16px

              .first-line
                display flex
                flex-direction row
                justify-content space-between
                align-items center

              .description-line
                display flex
                flex-direction row
                justify-content space-between
                align-items center
                width 80%

                .light-grey
                  color #7E727E
                  font-size 12px
                  font-style normal
                  font-weight 600
                  line-height 130%
                  letter-spacing -0.12px

              .dropdown-wrap
                width 80%
            
          .member-benefits
            border-top 1px solid #CBC6CB
            padding-top 14px
            margin 10px
            ul
              list-style disc
              li
                margin-top 6px
                white-space normal
                list-style-position inside
                padding-left 20px
                text-indent -20px
              li::marker
                color brand-color-1

          .dropdown-wrap
            &.version-b
              margin 10px 6%

            >>> .mr-dropdown
              .dropdown-wrap-new-theme
                border-radius 0
                border 1px solid brand-color-1
                
                .options
                  padding 0 10px
      
      .extra-tube
        border-top 1px solid #CBC6CB
        margin-top 26px
        padding-top 18px

        .extra-tube-title
          font-style normal
          font-weight 600
          line-height 120%
          letter-spacing -0.13px

        .extra-tube-checkbox
          padding 10px 12px
          background-color #F8F7F8
          display flex
          align-items center
          font-weight 600
          line-height 130%
          letter-spacing -0.13px
      
        >>> .mr-input-checkbox
          label
            line-height 0 !important

    @media mq-mobile
      .radio-block
        .radio-wrap
          .radio
            .dropdown-wrap
              &.version-b
                margin 10px 10%
</style>
  